import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, Chip, Grid, Card, CardContent,
  CardActions, Divider, Alert, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, IconButton, Tabs, Tab
} from '@mui/material';
import {
  Upload as UploadIcon, Download as DownloadIcon,
  CheckCircle as CheckCircleIcon, Assignment as AssignmentIcon,
  Visibility as VisibilityIcon, Star as StarIcon,
  ArrowBack as ArrowBackIcon, Save as SaveIcon,
  Delete as DeleteIcon, Description as DescriptionIcon,
  Close as CloseIcon, Compare as CompareIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { siteAPI } from '../services/api';
import { HyperFormula } from 'hyperformula';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

const BOQWorkbookPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'manager';
  const canEdit = isAdmin || isManager;

  const siteIdParam = searchParams.get('siteId');
  const siteNameParam = searchParams.get('siteName');

  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState(siteIdParam || '');
  const [workbooks, setWorkbooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [managers, setManagers] = useState([]);

  const [viewOpen, setViewOpen] = useState(false);
  const [viewWorkbook, setViewWorkbook] = useState(null);
  const [activeSheet, setActiveSheet] = useState(0);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const originalEditValue = useRef('');
  const userDidEdit = useRef(false);
  const [hasEdits, setHasEdits] = useState(false);
  const [hfInstance, setHfInstance] = useState(null);
  const [computedCells, setComputedCells] = useState({});

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadNotes, setUploadNotes] = useState('');
  const [uploadCheckedBy, setUploadCheckedBy] = useState('');
  const [uploadApprovedBy, setUploadApprovedBy] = useState('');
  const [uploading, setUploading] = useState(false);

  const [saveVersionOpen, setSaveVersionOpen] = useState(false);
  const [saveVersionCheckedBy, setSaveVersionCheckedBy] = useState('');
  const [saveVersionApprovedBy, setSaveVersionApprovedBy] = useState('');
  const [saveVersionNotes, setSaveVersionNotes] = useState('');
  const [savingVersion, setSavingVersion] = useState(false);

  const [compareOpen, setCompareOpen] = useState(false);
  const [compareWorkbook1, setCompareWorkbook1] = useState('');
  const [compareWorkbook2, setCompareWorkbook2] = useState('');
  const [compareSheet, setCompareSheet] = useState('');
  const [compareData, setCompareData] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);

  const [approveOpen, setApproveOpen] = useState(false);
  const [approveWorkbookId, setApproveWorkbookId] = useState(null);
  const [approveAction, setApproveAction] = useState('');
  const [approveData, setApproveData] = useState({ approvedById: '' });

  const [downloadOpen, setDownloadOpen] = useState(false);
  const [downloadWorkbookId, setDownloadWorkbookId] = useState(null);

  const [trashOpen, setTrashOpen] = useState(false);
  const [trashItems, setTrashItems] = useState([]);
  const [trashLoading, setTrashLoading] = useState(false);

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const fetchSites = useCallback(async () => {
    try { const res = await siteAPI.getAll(); setSites(res.data.data.sites); } catch (e) { console.error(e); }
  }, []);

  const fetchWorkbooks = useCallback(async (siteId) => {
    if (!siteId) return;
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API_BASE_URL}/boq-workbooks/site/${siteId}`, authHeaders());
      setWorkbooks(res.data.data.workbooks);
    } catch (e) { setError('Failed to load BOQ workbooks'); } finally { setLoading(false); }
  }, []);

  const fetchManagers = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/users?role=manager`, authHeaders());
      setManagers(res.data.data?.users || []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchSites(); fetchManagers(); }, [fetchSites, fetchManagers]);
  useEffect(() => { if (selectedSiteId) fetchWorkbooks(selectedSiteId); }, [selectedSiteId, fetchWorkbooks]);

  const initHyperFormula = useCallback((workbook) => {
    if (!workbook?.sheets) return;
    const sheetsData = {};
    workbook.sheets.forEach(sheet => {
      let maxRow = 0, maxCol = 0;
      sheet.cells.forEach(cell => {
        if (cell.rowIndex > maxRow) maxRow = cell.rowIndex;
        if (cell.colIndex > maxCol) maxCol = cell.colIndex;
      });
      const grid = [];
      for (let r = 0; r <= maxRow; r++) { grid[r] = []; for (let c = 0; c <= maxCol; c++) grid[r][c] = ''; }
      sheet.cells.forEach(cell => { grid[cell.rowIndex][cell.colIndex] = cell.formula || (cell.value !== null ? cell.value : ''); });
      sheetsData[sheet.sheetName] = grid;
    });
    const hf = HyperFormula.buildFromSheets(sheetsData, { licenseKey: 'gpl-v3' });
    setHfInstance(hf);
    const computed = {};
    workbook.sheets.forEach((sheet, sheetIdx) => {
      computed[sheet.id] = {};
      sheet.cells.forEach(cell => {
        try {
          if (cell.formula) {
            const val = hf.getCellValue({ sheet: sheetIdx, row: cell.rowIndex, col: cell.colIndex });
            computed[sheet.id][`${cell.rowIndex}_${cell.colIndex}`] = val;
          } else {
            computed[sheet.id][`${cell.rowIndex}_${cell.colIndex}`] = cell.value;
          }
        } catch (e) { computed[sheet.id][`${cell.rowIndex}_${cell.colIndex}`] = cell.value; }
      });
    });
    setComputedCells(computed);
  }, []);

  const fetchWorkbookDetail = async (workbookId) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/boq-workbooks/${workbookId}`, authHeaders());
      const wb = res.data.data.workbook;
      setViewWorkbook(wb);
      setActiveSheet(0);
      setHasEdits(false);
      setEditingCell(null);
      setEditValue('');
      userDidEdit.current = false;
      initHyperFormula(wb);
    } catch (e) { setError('Failed to load workbook details'); }
  };

  const getCellDisplay = (cell, sheetId) => {
    if (!cell) return '';
    // For formula cells, use HyperFormula computed value
    if (cell.formula) {
      const computed = computedCells[sheetId]?.[`${cell.rowIndex}_${cell.colIndex}`];
      if (computed !== undefined && computed !== null) {
        if (typeof computed === 'object' && computed.type === 'ERROR') return cell.value || '';
        return String(computed);
      }
    }
    // For plain value cells, always use cell.value directly
    return cell.value || '';
  };

  const buildGrid = (cells) => {
    if (!cells || cells.length === 0) return { grid: [], maxRow: 0, maxCol: 0 };
    let maxRow = 0, maxCol = 0;
    cells.forEach(cell => {
      if (cell.rowIndex > maxRow) maxRow = cell.rowIndex;
      if (cell.colIndex > maxCol) maxCol = cell.colIndex;
    });
    const grid = Array.from({ length: maxRow + 1 }, () => Array(maxCol + 1).fill(null));
    cells.forEach(cell => { grid[cell.rowIndex][cell.colIndex] = cell; });
    return { grid, maxRow, maxCol };
  };

  // Auto column width based on content length
  const getColWidth = (grid, colIdx) => {
    let maxLen = 0;
    for (let r = 0; r < Math.min(grid.length, 30); r++) {
      const cell = grid[r]?.[colIdx];
      const val = String(cell?.value ?? cell?.formattedValue ?? '');
      if (val.length > maxLen) maxLen = val.length;
    }
    if (maxLen > 80) return 340;
    if (maxLen > 40) return 220;
    if (maxLen > 20) return 150;
    if (maxLen > 10) return 110;
    return 85;
  };

  // Only save if user actually typed AND value changed
  const handleCellEdit = async (cell, sheetIdx) => {
    if (!canEdit) return;
    if (!userDidEdit.current) {
      setEditingCell(null);
      setEditValue('');
      userDidEdit.current = false;
      return;
    }
    if (editValue === originalEditValue.current) {
      setEditingCell(null);
      setEditValue('');
      userDidEdit.current = false;
      return;
    }
    try {
      console.log('[BOQ] Saving cell id:', cell.id, '| value:', editValue);
      await axios.put(`${API_BASE_URL}/boq-workbooks/cell/${cell.id}`, {
        value: editValue,
        formula: editValue.startsWith('=') ? editValue : null
      }, authHeaders());
      console.log('[BOQ] Cell saved OK');
      if (hfInstance) {
        hfInstance.setCellContents({ sheet: sheetIdx, row: cell.rowIndex, col: cell.colIndex }, [[editValue]]);
        const newComputed = { ...computedCells };
        viewWorkbook.sheets.forEach((sheet, sIdx) => {
          sheet.cells.forEach(c => {
            try {
              const val = hfInstance.getCellValue({ sheet: sIdx, row: c.rowIndex, col: c.colIndex });
              if (!newComputed[sheet.id]) newComputed[sheet.id] = {};
              newComputed[sheet.id][`${c.rowIndex}_${c.colIndex}`] = val;
            } catch (e) { }
          });
        });
        setComputedCells(newComputed);
      }
      setViewWorkbook(prev => ({
        ...prev,
        sheets: prev.sheets.map((sheet, sIdx) => sIdx !== sheetIdx ? sheet : {
          ...sheet,
          cells: sheet.cells.map(c => c.id !== cell.id ? c : {
            ...c,
            value: editValue.startsWith('=') ? c.value : editValue,
            formula: editValue.startsWith('=') ? editValue : null
          })
        })
      }));
      setHasEdits(true);
      setSuccess('✓ Cell saved');
      setTimeout(() => setSuccess(''), 2000);
    } catch (e) {
      console.error('[BOQ] Cell save error:', e.response?.data || e.message);
      setError('Failed to update cell: ' + (e.response?.data?.message || e.message));
    } finally {
      setEditingCell(null);
      setEditValue('');
      userDidEdit.current = false;
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !selectedSiteId) return;
    setUploading(true); setError('');
    try {
      const formData = new FormData();
      formData.append('boqFile', uploadFile);
      formData.append('siteId', selectedSiteId);
      if (uploadNotes) formData.append('notes', uploadNotes);
      if (uploadCheckedBy) formData.append('checkedById', uploadCheckedBy);
      if (uploadApprovedBy) formData.append('approvedById', uploadApprovedBy);
      const res = await axios.post(`${API_BASE_URL}/boq-workbooks/upload`, formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'multipart/form-data' }
      });
      setSuccess(`${res.data.data.workbook.versionLabel} uploaded with ${res.data.data.sheets.length} sheets!`);
      setUploadOpen(false); setUploadFile(null); setUploadNotes(''); setUploadCheckedBy(''); setUploadApprovedBy('');
      fetchWorkbooks(selectedSiteId);
    } catch (e) { setError(e.response?.data?.message || 'Upload failed'); } finally { setUploading(false); }
  };

  const handleSaveAsNewVersion = async () => {
    if (!saveVersionApprovedBy) { setError('Please select Approved By'); return; }
    setSavingVersion(true);
    try {
      // First sync all computed formula values to DB
      if (hfInstance && viewWorkbook) {
        const cellUpdates = [];
        viewWorkbook.sheets.forEach((sheet, sheetIdx) => {
          sheet.cells.forEach(cell => {
            if (cell.formula) {
              try {
                const val = hfInstance.getCellValue({ sheet: sheetIdx, row: cell.rowIndex, col: cell.colIndex });
                if (val !== null && val !== undefined && !(typeof val === 'object' && val.type === 'ERROR')) {
                  cellUpdates.push({ id: cell.id, value: String(val) });
                }
              } catch (e) { }
            }
          });
        });
        if (cellUpdates.length > 0) {
          await axios.put(`${API_BASE_URL}/boq-workbooks/cells/bulk`, { cells: cellUpdates }, authHeaders());
        }
      }
      const res = await axios.post(`${API_BASE_URL}/boq-workbooks/${viewWorkbook.id}/save-version`,
        { checkedById: saveVersionCheckedBy || null, approvedById: saveVersionApprovedBy, notes: saveVersionNotes || null },
        authHeaders());
      setSuccess(`${res.data.data.workbook.versionLabel} saved!`);
      setSaveVersionOpen(false); setViewOpen(false); fetchWorkbooks(selectedSiteId);
    } catch (e) { setError(e.response?.data?.message || 'Failed to save version'); } finally { setSavingVersion(false); }
  };

  const handleApprove = async () => {
    try {
      await axios.put(`${API_BASE_URL}/boq-workbooks/${approveWorkbookId}/approve`,
        { action: approveAction, approvedById: approveData.approvedById }, authHeaders());
      setSuccess(`Workbook ${approveAction} successful!`); setApproveOpen(false); fetchWorkbooks(selectedSiteId);
    } catch (e) { setError('Action failed'); }
  };

  const handleDownload = async (workbookId, withFormulas) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/boq-workbooks/${workbookId}/download?withFormulas=${withFormulas}`,
        { ...authHeaders(), responseType: 'blob' });
      const wb = workbooks.find(w => w.id === workbookId) || viewWorkbook;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url; link.setAttribute('download', `${wb?.versionLabel || 'BOQ'}.xlsx`);
      document.body.appendChild(link); link.click(); link.remove();
      setDownloadOpen(false);
    } catch (e) { setError('Download failed'); }
  };

  const handleCompare = async () => {
    if (!compareWorkbook1 || !compareWorkbook2 || !compareSheet) return;
    setCompareLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/boq-workbooks/compare/${compareWorkbook1}/${compareWorkbook2}?sheetName=${compareSheet}`, authHeaders());
      setCompareData(res.data.data);
    } catch (e) { setError('Compare failed'); } finally { setCompareLoading(false); }
  };

  const fetchTrash = async () => {
    setTrashLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/boq-workbooks/trash?siteId=${selectedSiteId}`, authHeaders());
      setTrashItems(res.data.data.workbooks);
    } catch (e) { setError('Failed to load trash'); }
    finally { setTrashLoading(false); }
  };

  const handleSoftDelete = async (workbookId) => {
    if (!window.confirm('Move this BOQ to trash?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/boq-workbooks/${workbookId}`, authHeaders());
      setSuccess('Moved to trash'); fetchWorkbooks(selectedSiteId);
    } catch (e) { setError('Failed to delete'); }
  };

  const handleHardDelete = async (workbookId) => {
    if (!window.confirm('Permanently delete? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_BASE_URL}/boq-workbooks/${workbookId}/hard`, authHeaders());
      setSuccess('Permanently deleted'); fetchTrash();
    } catch (e) { setError('Failed to permanently delete'); }
  };

  const handleRestore = async (workbookId) => {
    try {
      await axios.put(`${API_BASE_URL}/boq-workbooks/${workbookId}/restore`, {}, authHeaders());
      setSuccess('Workbook restored'); fetchTrash(); fetchWorkbooks(selectedSiteId);
    } catch (e) { setError('Failed to restore'); }
  };

  const normalizeCellValue = (cell) => {
    const raw = cell?.value ?? cell?.formattedValue ?? '';
    const str = String(raw).trim();
    const num = Number(str);
    if (str !== '' && !isNaN(num)) return String(Math.round(num * 1e9) / 1e9);
    return str.toLowerCase();
  };

  const selectedSiteName = siteNameParam || sites.find(s => s.id === selectedSiteId)?.siteName || '';
  const statusColors = { draft: 'default', approved: 'success', final: 'warning' };
  const colLabel = (i) => i < 26 ? String.fromCharCode(65 + i) : String.fromCharCode(64 + Math.floor(i / 26)) + String.fromCharCode(65 + (i % 26));

  const headerCornerStyle = {
    background: '#e2e3e5', border: '1px solid #b0b0b0',
    borderBottom: '2px solid #888', borderRight: '2px solid #888',
    padding: '4px 6px', textAlign: 'center', fontWeight: 700,
    position: 'sticky', top: 0, left: 0, zIndex: 4,
    width: 40, minWidth: 40, fontSize: '11px', color: '#444', userSelect: 'none',
  };

  const getColHeaderStyle = (width) => ({
    background: '#e2e3e5', border: '1px solid #b0b0b0',
    borderBottom: '2px solid #888', padding: '4px 6px',
    textAlign: 'center', fontWeight: 700, position: 'sticky', top: 0, zIndex: 2,
    width, minWidth: width, fontSize: '11px', color: '#444',
    userSelect: 'none', whiteSpace: 'nowrap',
  });

  const rowNumCellStyle = {
    background: '#e2e3e5', border: '1px solid #d0d0d0',
    borderRight: '2px solid #888', padding: '2px 6px',
    textAlign: 'center', fontWeight: 600, position: 'sticky', left: 0, zIndex: 1,
    width: 40, minWidth: 40, fontSize: '10px', color: '#555', userSelect: 'none',
  };

  // FIXED: border-based highlight (no outline), only on the one editing cell
  const getDataCellStyle = (isEditing, isNum, colWidth) => ({
    border: isEditing ? '2px solid #107c41' : '1px solid #d0d0d0',
    padding: '3px 6px',
    width: colWidth,
    minWidth: colWidth,
    maxWidth: colWidth,
    whiteSpace: 'pre-wrap',        // Excel-like text wrap
    wordBreak: 'break-word',
    fontSize: '11px',
    fontFamily: 'Calibri, Arial, sans-serif',
    lineHeight: '1.4',
    verticalAlign: 'top',
    textAlign: isNum ? 'right' : 'left',
    cursor: canEdit ? 'cell' : 'default',
    backgroundColor: isEditing ? '#e8f4fd' : 'white',
    boxSizing: 'border-box',
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/boq')} size="small"><ArrowBackIcon /></IconButton>
          <Box>
            <Typography variant="h4">BOQ Management</Typography>
            {selectedSiteName && <Typography variant="body2" color="text.secondary">{selectedSiteName}</Typography>}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {workbooks.length >= 2 && (
            <Button variant="outlined" startIcon={<CompareIcon />} onClick={() => setCompareOpen(true)}>Compare Versions</Button>
          )}
          {isAdmin && selectedSiteId && (
            <Button variant="outlined" color="error" startIcon={<DeleteIcon />}
              onClick={() => { setTrashOpen(true); fetchTrash(); }}>
              Trash
            </Button>
          )}
          {canEdit && selectedSiteId && (
            <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => setUploadOpen(true)} sx={{ borderColor: '#2563eb', color: '#2563eb', fontWeight: 700, borderRadius: '10px', textTransform: 'none', '&:hover': { background: '#eff6ff' } }}>Upload BOQ</Button>
          )}
        </Box>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {!siteIdParam && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <TextField select fullWidth label="Select Site" value={selectedSiteId} onChange={(e) => setSelectedSiteId(e.target.value)}>
            {sites.map(s => <MenuItem key={s.id} value={s.id}>{s.siteName} — {s.location}</MenuItem>)}
          </TextField>
        </Paper>
      )}

      {!selectedSiteId && <Alert severity="info">Please select a site to view BOQs.</Alert>}
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}

      {!loading && selectedSiteId && (
        workbooks.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <AssignmentIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">No BOQs uploaded yet</Typography>
            {canEdit && <Button variant="contained" sx={{ mt: 2 }} startIcon={<UploadIcon />} onClick={() => setUploadOpen(true)}>Upload First BOQ</Button>}
          </Paper>
        ) : (
          <Grid container spacing={2.5}>
            {workbooks.map((wb) => (
              <Grid item xs={12} md={6} lg={4} key={wb.id}>
                <Box sx={{
                  background: 'white',
                  borderRadius: '14px',
                  border: '1px solid #e2e8f0',
                  overflow: 'hidden',
                  position: 'relative',
                  transition: 'box-shadow 0.2s, transform 0.2s',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
                  '&:hover': { boxShadow: '0 8px 30px rgba(0,0,0,0.10)', transform: 'translateY(-2px)' }
                }}>
                  <Box sx={{ p: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ width: 42, height: 42, borderRadius: '10px', background: wb.status === 'final' ? 'rgba(245,158,11,0.1)' : '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <DescriptionIcon sx={{ color: wb.status === 'final' ? '#f59e0b' : '#3b82f6', fontSize: 22 }} />
                        </Box>
                        <Box>
                          <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#0f172a', lineHeight: 1.2 }}>{wb.versionLabel}</Typography>
                          <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', mt: 0.3 }}>
                            {wb.createdAt ? new Date(wb.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Date unknown'}
                          </Typography>
                        </Box>
                      </Box>
                      <span style={{
                        fontSize: '0.7rem', fontWeight: 700, padding: '3px 10px', borderRadius: 99,
                        background: wb.status === 'final' ? '#fffbeb' : wb.status === 'approved' ? '#f0fdf4' : '#f1f5f9',
                        color: wb.status === 'final' ? '#d97706' : wb.status === 'approved' ? '#16a34a' : '#64748b',
                        border: `1px solid ${wb.status === 'final' ? '#fde68a' : wb.status === 'approved' ? '#bbf7d0' : '#e2e8f0'}`,
                        letterSpacing: '0.04em', textTransform: 'uppercase'
                      }}>
                        {wb.status}
                      </span>
                    </Box>

                    {wb.originalFilename && (
                      <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', mb: 1.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        📄 {wb.originalFilename}
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                      {wb.sheets?.map(sheet => (
                        <span key={sheet.id} style={{ fontSize: '0.7rem', fontWeight: 600, padding: '3px 9px', borderRadius: 6, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' }}>
                          {sheet.sheetName}
                        </span>
                      ))}
                    </Box>

                    <Box sx={{ pt: 1.5, borderTop: '1px solid #f1f5f9' }}>
                      {wb.uploader && (
                        <Typography sx={{ fontSize: '0.75rem', color: '#64748b', mb: 0.5 }}>
                          <span style={{ color: '#94a3b8' }}>Uploaded by</span> <strong>{wb.uploader.name}</strong>
                        </Typography>
                      )}
                      {wb.approver && (
                        <Typography sx={{ fontSize: '0.75rem', color: '#16a34a', fontWeight: 600 }}>✅ Approved by {wb.approver.name}</Typography>
                      )}
                      {wb.notes && (
                        <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', mt: 0.5, fontStyle: 'italic' }}>📝 {wb.notes}</Typography>
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ px: 2.5, pb: 2.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <button style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 14px', borderRadius: 7, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', border: 'none', background: '#eff6ff', color: '#2563eb', transition: 'all 0.15s' }}
                      onClick={() => { fetchWorkbookDetail(wb.id); setViewOpen(true); }}>
                      View
                    </button>
                    <button style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 14px', borderRadius: 7, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', border: 'none', background: '#f0fdf4', color: '#16a34a', transition: 'all 0.15s' }}
                      onClick={() => { setDownloadWorkbookId(wb.id); setDownloadOpen(true); }}>
                      Download
                    </button>
                    {canEdit && wb.status === 'draft' && (
                      <button style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 14px', borderRadius: 7, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', border: 'none', background: '#f0fdf4', color: '#16a34a' }}
                        onClick={() => { setApproveWorkbookId(wb.id); setApproveAction('approve'); setApproveOpen(true); }}>
                        Approve
                      </button>
                    )}
                    {canEdit && wb.status === 'approved' && (
                      <button style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 14px', borderRadius: 7, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', border: 'none', background: '#fffbeb', color: '#d97706' }}
                        onClick={() => { setApproveWorkbookId(wb.id); setApproveAction('final'); setApproveOpen(true); }}>
                        Mark Final
                      </button>
                    )}
                    {canEdit && (
                      <button style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 14px', borderRadius: 7, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', border: 'none', background: '#fef2f2', color: '#dc2626' }}
                        onClick={() => handleSoftDelete(wb.id)}>
                        Delete
                      </button>
                    )}
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        )
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Multi-Sheet BOQ</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>All sheets in the Excel file will be imported automatically.</Alert>
          <Button variant="outlined" component="label" fullWidth startIcon={<UploadIcon />} sx={{ mb: 2 }}>
            {uploadFile ? uploadFile.name : 'Select BOQ Excel File (.xlsx, .xls)'}
            <input type="file" hidden accept=".xlsx,.xls" onChange={(e) => setUploadFile(e.target.files[0])} />
          </Button>
          <TextField fullWidth label="Checked By (Manager)" select value={uploadCheckedBy} onChange={(e) => setUploadCheckedBy(e.target.value)} sx={{ mb: 2 }}>
            <MenuItem value="">-- Optional --</MenuItem>
            {managers.map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
          </TextField>
          <TextField fullWidth label="Approved By (Manager) *" select value={uploadApprovedBy} onChange={(e) => setUploadApprovedBy(e.target.value)} sx={{ mb: 2 }}>
            <MenuItem value="">-- Select Manager --</MenuItem>
            {managers.map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
          </TextField>
          <TextField fullWidth multiline rows={2} label="Notes (Optional)" value={uploadNotes} onChange={(e) => setUploadNotes(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpload} disabled={!uploadFile || !uploadApprovedBy || uploading}
            startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}>
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View/Edit Dialog */}
      <Dialog
        open={viewOpen}
        onClose={() => { setViewOpen(false); setViewWorkbook(null); setEditingCell(null); setHasEdits(false); }}
        maxWidth="xl"
        fullWidth
        disableEnforceFocus
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssignmentIcon />
              <Typography variant="h6">{viewWorkbook?.versionLabel} — {selectedSiteName}</Typography>
              {viewWorkbook && <Chip label={viewWorkbook.status} color={statusColors[viewWorkbook.status]} size="small" />}
              {hasEdits && <Chip label="Has edits" color="warning" size="small" variant="outlined" />}
            </Box>
            <IconButton onClick={() => { setViewOpen(false); setViewWorkbook(null); setHasEdits(false); }}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {!viewWorkbook ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <Box>
              <Tabs
                value={activeSheet}
                onChange={(e, v) => {
                  setActiveSheet(v);
                  setEditingCell(null);
                  setEditValue('');
                  userDidEdit.current = false;
                }}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ borderBottom: 1, borderColor: 'divider', px: 2, bgcolor: 'background.paper' }}
              >
                {viewWorkbook.sheets?.map((sheet, idx) => <Tab key={sheet.id} label={sheet.sheetName} value={idx} />)}
              </Tabs>
              {canEdit && (
                <Typography variant="caption" color="text.secondary"
                  sx={{
                    px: 2, py: 0.75, display: 'block',
                    bgcolor: viewWorkbook.isEditable ? '#fffde7' : '#fce4ec',
                    borderBottom: `1px solid ${viewWorkbook.isEditable ? '#f0e68c' : '#f48fb1'}`
                  }}>
                  {viewWorkbook.isEditable
                    ? '💡 Double-click any cell to edit. Press Enter to save, Escape to cancel.'
                    : '🔒 This version is read-only. Save as New Version to make changes.'}
                </Typography>
              )}
              {viewWorkbook.sheets?.map((sheet, sheetIdx) => (
                <Box key={sheet.id} hidden={activeSheet !== sheetIdx} sx={{ overflow: 'auto', maxHeight: '62vh' }}>
                  {activeSheet === sheetIdx && (() => {
                    const { grid, maxCol } = buildGrid(sheet.cells);
                    const colWidths = Array.from({ length: maxCol + 1 }, (_, i) => getColWidth(grid, i));
                    return (
                      <table style={{
                        borderCollapse: 'collapse',
                        minWidth: 'max-content',
                        fontSize: '11px',
                        fontFamily: 'Calibri, Arial, sans-serif',
                        backgroundColor: 'white',
                        tableLayout: 'fixed',
                      }}>
                        <thead>
                          <tr>
                            <th style={headerCornerStyle}>#</th>
                            {Array.from({ length: maxCol + 1 }, (_, i) => (
                              <th key={i} style={getColHeaderStyle(colWidths[i])}>{colLabel(i)}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {grid.map((row, rowIdx) => (
                            <tr key={rowIdx}>
                              <td style={rowNumCellStyle}>{rowIdx + 1}</td>
                              {row.map((cell, colIdx) => {
                                // FIXED: compare by cell ID to avoid false positives
                                const isEditing = !!(editingCell && cell && editingCell.id === cell.id);
                                const displayVal = getCellDisplay(cell, sheet.id);
                                const isNum = displayVal !== '' && !isNaN(Number(displayVal)) && displayVal.trim() !== '';
                                return (
                                  <td
                                    key={colIdx}
                                    style={getDataCellStyle(isEditing, isNum, colWidths[colIdx])}
                                    onDoubleClick={() => {
                                      if (!canEdit || !cell || !viewWorkbook.isEditable) return;
                                      const val = cell.formula || cell.value || '';
                                      setEditingCell(cell);
                                      setEditValue(val);
                                      originalEditValue.current = val;
                                      userDidEdit.current = false;
                                    }}
                                    onClick={() => {
                                      if (!canEdit || !cell || !viewWorkbook.isEditable || window.innerWidth > 768) return;
                                      const val = cell.formula || cell.value || '';
                                      setEditingCell(cell);
                                      setEditValue(val);
                                      originalEditValue.current = val;
                                      userDidEdit.current = false;
                                    }}
                                    title={displayVal}
                                  >
                                    {isEditing ? (
                                      <textarea
                                        autoFocus
                                        value={editValue}
                                        rows={3}
                                        onChange={(e) => {
                                          setEditValue(e.target.value);
                                          userDidEdit.current = true;
                                        }}
                                        onBlur={() => handleCellEdit(cell, sheetIdx)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            handleCellEdit(cell, sheetIdx);
                                          }
                                          if (e.key === 'Escape') {
                                            setEditingCell(null);
                                            setEditValue('');
                                            userDidEdit.current = false;
                                          }
                                        }}
                                        style={{
                                          width: '100%',
                                          border: 'none',
                                          outline: 'none',
                                          fontSize: '11px',
                                          fontFamily: 'Calibri, Arial, sans-serif',
                                          padding: '0',
                                          background: 'transparent',
                                          resize: 'none',
                                          lineHeight: '1.4',
                                        }}
                                      />
                                    ) : (
                                      <span style={{ display: 'block', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                        {displayVal}
                                      </span>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    );
                  })()}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {viewWorkbook && canEdit && (
            <Button variant="contained" startIcon={<SaveIcon />} onClick={() => setSaveVersionOpen(true)}>
              Save as New Version
            </Button>
          )}
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => {
            if (viewWorkbook) { setDownloadWorkbookId(viewWorkbook.id); setDownloadOpen(true); }
          }}>Download</Button>
          <Button onClick={() => { setViewOpen(false); setViewWorkbook(null); setHasEdits(false); }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Save Version Dialog */}
      <Dialog open={saveVersionOpen} onClose={() => setSaveVersionOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Save as New Version</DialogTitle>
        <DialogContent>
          <TextField select fullWidth label="Checked By (Manager)" value={saveVersionCheckedBy} onChange={(e) => setSaveVersionCheckedBy(e.target.value)} sx={{ mb: 2, mt: 1 }}>
            <MenuItem value="">-- Optional --</MenuItem>
            {managers.map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
          </TextField>
          <TextField select fullWidth label="Approved By (Manager) *" value={saveVersionApprovedBy} onChange={(e) => setSaveVersionApprovedBy(e.target.value)} sx={{ mb: 2 }}>
            <MenuItem value="">-- Select Manager --</MenuItem>
            {managers.map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
          </TextField>
          <TextField fullWidth multiline rows={2} label="Notes (Optional)" value={saveVersionNotes} onChange={(e) => setSaveVersionNotes(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveVersionOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveAsNewVersion} disabled={!saveVersionApprovedBy || savingVersion}>
            {savingVersion ? 'Saving...' : 'Save Version'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={approveOpen} onClose={() => setApproveOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{approveAction === 'approve' ? '✅ Approve BOQ' : '⭐ Mark as Final'}</DialogTitle>
        <DialogContent>
          {approveAction === 'approve' && (
            <TextField select fullWidth label="Approved By *" value={approveData.approvedById} onChange={(e) => setApproveData({ approvedById: e.target.value })} sx={{ mt: 1 }}>
              <MenuItem value="">-- Select Manager --</MenuItem>
              {managers.map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
            </TextField>
          )}
          {approveAction === 'final' && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              This will mark this BOQ as Final. Any previously Final BOQ will be changed to Approved.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveOpen(false)}>Cancel</Button>
          <Button variant="contained" color={approveAction === 'final' ? 'warning' : 'success'} onClick={handleApprove}
            disabled={approveAction === 'approve' && !approveData.approvedById}>Confirm</Button>
        </DialogActions>
      </Dialog>

      {/* Download Dialog */}
      <Dialog open={downloadOpen} onClose={() => setDownloadOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Download BOQ</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Button variant="contained" onClick={() => handleDownload(downloadWorkbookId, true)} fullWidth>📊 Download with Formulas</Button>
            <Button variant="outlined" onClick={() => handleDownload(downloadWorkbookId, false)} fullWidth>📋 Download as Plain Values</Button>
          </Box>
        </DialogContent>
        <DialogActions><Button onClick={() => setDownloadOpen(false)}>Cancel</Button></DialogActions>
      </Dialog>

      {/* Compare Dialog */}
      <Dialog open={compareOpen} onClose={() => { setCompareOpen(false); setCompareData(null); }} maxWidth="xl" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="h6">Compare Versions</Typography>
            <IconButton onClick={() => { setCompareOpen(false); setCompareData(null); }}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <TextField select fullWidth label="Version 1 (Old)" size="small" value={compareWorkbook1} onChange={(e) => setCompareWorkbook1(e.target.value)}>
                {workbooks.map(wb => <MenuItem key={wb.id} value={wb.id}>{wb.versionLabel}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select fullWidth label="Version 2 (New)" size="small" value={compareWorkbook2} onChange={(e) => setCompareWorkbook2(e.target.value)}>
                {workbooks.map(wb => <MenuItem key={wb.id} value={wb.id}>{wb.versionLabel}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select fullWidth label="Select Sheet" size="small" value={compareSheet} onChange={(e) => setCompareSheet(e.target.value)}>
                {(workbooks.find(w => w.id === compareWorkbook1)?.sheets || []).map(s => (
                  <MenuItem key={s.id} value={s.sheetName}>{s.sheetName}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Button variant="contained" onClick={handleCompare}
              disabled={!compareWorkbook1 || !compareWorkbook2 || !compareSheet || compareLoading}>
              {compareLoading ? 'Comparing...' : 'Compare'}
            </Button>
            {compareData && (
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 16, height: 16, bgcolor: '#fff0b3', border: '1px solid #ffc107', borderRadius: 0.5 }} />
                  <Typography variant="caption" fontWeight={600}>Changed in old</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 16, height: 16, bgcolor: '#b7f0c8', border: '1px solid #28a745', borderRadius: 0.5 }} />
                  <Typography variant="caption" fontWeight={600}>Changed in new</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 16, height: 16, bgcolor: 'white', border: '1px solid #ccc', borderRadius: 0.5 }} />
                  <Typography variant="caption" fontWeight={600}>Unchanged</Typography>
                </Box>
              </Box>
            )}
          </Box>

          {compareData && (
            <Grid container spacing={1}>
              <Grid item xs={12} md={6}>
                <Box sx={{ bgcolor: '#fff8e1', border: '1px solid #ffc107', borderRadius: '4px 4px 0 0', px: 2, py: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700} color="#856404">
                    📄 {compareData.version1.versionLabel} (Old)
                  </Typography>
                </Box>
                <Box sx={{ overflow: 'auto', maxHeight: '55vh', border: '1px solid #ffc107', borderTop: 'none', borderRadius: '0 0 4px 4px' }}>
                  <table style={{ borderCollapse: 'collapse', fontSize: '11px', fontFamily: 'Calibri, Arial, sans-serif', minWidth: 'max-content' }}>
                    <tbody>
                      {(() => {
                        const sheet1 = compareData.version1.sheets?.[0];
                        const sheet2 = compareData.version2.sheets?.[0];
                        if (!sheet1) return <tr><td style={{ padding: 8 }}>No data</td></tr>;
                        const { grid: grid1 } = buildGrid(sheet1.cells);
                        const { grid: grid2 } = buildGrid(sheet2?.cells || []);
                        const cWidths = Array.from({ length: (grid1[0]?.length || 0) }, (_, i) => getColWidth(grid1, i));
                        return grid1.map((row, rIdx) => (
                          <tr key={rIdx}>
                            <td style={{
                              background: '#e2e3e5', border: '1px solid #ccc', borderRight: '2px solid #aaa',
                              padding: '2px 5px', textAlign: 'center', fontWeight: 700,
                              minWidth: 32, fontSize: '10px', position: 'sticky', left: 0, color: '#555',
                            }}>{rIdx + 1}</td>
                            {row.map((cell, cIdx) => {
                              const cell2 = grid2[rIdx]?.[cIdx];
                              const val1 = normalizeCellValue(cell);
                              const val2 = normalizeCellValue(cell2);
                              const changed = val1 !== val2;
                              const displayVal = String(cell?.formattedValue ?? cell?.value ?? '');
                              return (
                                <td key={cIdx} style={{
                                  border: '1px solid #e0e0e0', padding: '3px 6px',
                                  width: cWidths[cIdx] || 85, minWidth: cWidths[cIdx] || 85,
                                  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                                  fontSize: '11px', lineHeight: '1.4', verticalAlign: 'top',
                                  backgroundColor: changed ? '#fff0b3' : 'white',
                                  fontWeight: changed ? 700 : 400,
                                  color: changed ? '#7a5c00' : 'inherit',
                                }} title={displayVal}>
                                  {displayVal}
                                </td>
                              );
                            })}
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ bgcolor: '#e8f5e9', border: '1px solid #28a745', borderRadius: '4px 4px 0 0', px: 2, py: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700} color="#155724">
                    📄 {compareData.version2.versionLabel} (New)
                  </Typography>
                </Box>
                <Box sx={{ overflow: 'auto', maxHeight: '55vh', border: '1px solid #28a745', borderTop: 'none', borderRadius: '0 0 4px 4px' }}>
                  <table style={{ borderCollapse: 'collapse', fontSize: '11px', fontFamily: 'Calibri, Arial, sans-serif', minWidth: 'max-content' }}>
                    <tbody>
                      {(() => {
                        const sheet1 = compareData.version1.sheets?.[0];
                        const sheet2 = compareData.version2.sheets?.[0];
                        if (!sheet2) return <tr><td style={{ padding: 8 }}>No data</td></tr>;
                        const { grid: grid2 } = buildGrid(sheet2.cells);
                        const { grid: grid1 } = buildGrid(sheet1?.cells || []);
                        const cWidths = Array.from({ length: (grid2[0]?.length || 0) }, (_, i) => getColWidth(grid2, i));
                        return grid2.map((row, rIdx) => (
                          <tr key={rIdx}>
                            <td style={{
                              background: '#e2e3e5', border: '1px solid #ccc', borderRight: '2px solid #aaa',
                              padding: '2px 5px', textAlign: 'center', fontWeight: 700,
                              minWidth: 32, fontSize: '10px', position: 'sticky', left: 0, color: '#555',
                            }}>{rIdx + 1}</td>
                            {row.map((cell, cIdx) => {
                              const cell1 = grid1[rIdx]?.[cIdx];
                              const val2 = normalizeCellValue(cell);
                              const val1 = normalizeCellValue(cell1);
                              const changed = val1 !== val2;
                              const displayVal = String(cell?.formattedValue ?? cell?.value ?? '');
                              return (
                                <td key={cIdx} style={{
                                  border: '1px solid #e0e0e0', padding: '3px 6px',
                                  width: cWidths[cIdx] || 85, minWidth: cWidths[cIdx] || 85,
                                  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                                  fontSize: '11px', lineHeight: '1.4', verticalAlign: 'top',
                                  backgroundColor: changed ? '#b7f0c8' : 'white',
                                  fontWeight: changed ? 700 : 400,
                                  color: changed ? '#0a4a1f' : 'inherit',
                                }} title={displayVal}>
                                  {displayVal}
                                </td>
                              );
                            })}
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
      </Dialog>
      {/* Trash Dialog */}
      <Dialog open={trashOpen} onClose={() => setTrashOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="h6">🗑️ Trash</Typography>
            <IconButton onClick={() => setTrashOpen(false)}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {trashLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : trashItems.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>Trash is empty</Typography>
          ) : (
            trashItems.map(wb => (
              <Box key={wb.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box>
                  <Typography fontWeight={700}>{wb.versionLabel}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">{wb.originalFilename}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Deleted on {new Date(wb.deletedAt).toLocaleDateString('en-IN')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                    {wb.sheets?.map(s => <Chip key={s.id} label={s.sheetName} size="small" variant="outlined" />)}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" color="success" onClick={() => handleRestore(wb.id)}>Restore</Button>
                  {isAdmin && (
                    <Button size="small" color="error" onClick={() => handleHardDelete(wb.id)}>Delete Forever</Button>
                  )}
                </Box>
              </Box>
            ))
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default BOQWorkbookPage;